import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { useGetGuestJourneyOffersQuery } from 'api/reports-service/guest-journey-reports';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferImpactBootstrapData } from '../guest-journey.hooks';

export type OfferImpactBootstrapProps = {
  /** Rendered only once at least one GJ offer has resolved. */
  children: (data: OfferImpactBootstrapData) => ReactNode;
};

/**
 * Blocks the Offer Impact tab until the GJ-scoped offer list is in hand
 * (`GET guest-journey/offers`). The summary + sankey endpoints require a real
 * `offerIds` and 400 without one, so fetching the offers up front lets the Offer
 * filter start on a valid, synchronous default (the first offer) instead of a
 * racy `asyncOptions` on a required query param — the same approach as the
 * Benchmarks bootstrap. Shows a loader while fetching, an info alert when the
 * client has no offers with impact data yet, and a retryable error alert on
 * failure; `children` runs only when there is at least one offer.
 */
const OfferImpactBootstrap = ({ children }: OfferImpactBootstrapProps) => {
  const { t } = useTranslation();

  const offers = useGetGuestJourneyOffersQuery({});

  if (offers.data) {
    if (offers.data.length === 0) {
      return (
        <Alert severity="info" data-testid="OfferImpact.Empty">
          {t('page.guestJourney.offerImpact.empty')}
        </Alert>
      );
    }
    const offerOptions = offers.data.map(offer => ({
      id: offer.offerId,
      label: offer.offerName,
    }));
    return <>{children({ offerOptions, offerDefault: offerOptions[0].id })}</>;
  }

  if (offers.isFetching) {
    return (
      <Stack
        alignItems="center"
        paddingY={8}
        data-testid="OfferImpact.BootstrapLoading"
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Alert
      severity="error"
      data-testid="OfferImpact.BootstrapError"
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => offers.refetch()}
          data-testid="OfferImpact.BootstrapError.Retry"
        >
          {t('page.guestJourney.offerImpact.bootstrapError.retry')}
        </Button>
      }
    >
      {t('page.guestJourney.offerImpact.bootstrapError.message')}
    </Alert>
  );
};

export default OfferImpactBootstrap;
